"use client";

import { motion, useInView, animate } from "motion/react";
import { useLocale } from "next-intl";
import { useEffect, useMemo, useRef, useState } from "react";

export const fadeUp = {
    hidden: { opacity: 0, y: 24 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1] } },
};

export const fadeUpStagger = (delay = 0) => ({
    hidden: { opacity: 0, y: 24 },
    visible: {
        opacity: 1,
        y: 0,
        transition: { duration: 0.7, delay, ease: [0.22, 1, 0.36, 1] },
    },
});

export function SectionMarker({ label }) {
    return (
        <div className="flex items-center mb-10 text-[10px] uppercase tracking-[0.25em] text-gray-500 dark:text-white/55">
            <div className="flex items-center gap-3">
                <span className="w-1.5 h-1.5 rounded-full bg-primary shadow-[0_0_8px_rgba(232,81,29,0.4)]" />
                <span>{label}</span>
            </div>
        </div>
    );
}

export function Eyebrow({ children }) {
    return (
        <div className="inline-flex items-center gap-3 mb-6">
            <span className="block w-8 h-px bg-primary" />
            <span className="text-[11px] uppercase tracking-[0.28em] font-medium text-primary">
                {children}
            </span>
        </div>
    );
}

export function GradientText({ children, className = "" }) {
    return (
        <span
            className={`bg-clip-text text-transparent bg-gradient-to-r from-[#FF7A1A] via-[#FF3B5C] to-[#E51578] ${className}`}
        >
            {children}
        </span>
    );
}

export function EditorialTitle({ children, className = "" }) {
    return (
        <h2
            className={`font-serif font-bold text-gray-900 dark:text-white leading-[1.05] tracking-tight ${className}`}
            style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
        >
            {children}
        </h2>
    );
}

export function SectionShell({ children, label, className = "", id }) {
    return (
        <section
            id={id}
            className={`relative max-w-[1200px] mx-auto px-5 sm:px-6 lg:px-10 py-16 sm:py-20 lg:py-32 ${className}`}
        >
            {label ? <SectionMarker label={label} /> : null}
            {children}
        </section>
    );
}

export function AnimatedNumber({
    value,
    duration = 1.6,
    format,
    className = "",
    delay = 0,
}) {
    const locale = useLocale();
    const ref = useRef(null);
    const inView = useInView(ref, { once: true, amount: 0.4 });
    const formatter = useMemo(
        () => format || ((v) => Math.round(v).toLocaleString(locale)),
        [format, locale],
    );
    const [text, setText] = useState(() => formatter(0));

    useEffect(() => {
        if (!inView) return;
        const controls = animate(0, value, {
            duration,
            delay,
            ease: [0.16, 1, 0.3, 1],
            onUpdate: (latest) => setText(formatter(latest)),
        });
        return () => controls.stop();
    }, [inView, value, duration, delay, formatter]);

    return (
        <span ref={ref} className={className}>
            {text}
        </span>
    );
}

export function AnimatedBar({ percent, className = "", from = "from-gray-300 dark:from-white/30", to = "to-gray-300 dark:to-white/30", delay = 0 }) {
    const ref = useRef(null);
    const inView = useInView(ref, { once: true, amount: 0.5 });
    return (
        <div ref={ref} className={`relative h-1.5 rounded-full bg-gray-100 dark:bg-white/10 overflow-hidden ${className}`}>
            <motion.div
                initial={{ width: 0 }}
                animate={inView ? { width: `${Math.min(100, Math.max(0, percent))}%` } : { width: 0 }}
                transition={{ duration: 1.4, delay, ease: [0.16, 1, 0.3, 1] }}
                className={`absolute inset-y-0 left-0 bg-gradient-to-r ${from} ${to} rounded-full`}
            />
        </div>
    );
}

export function useUserYear() {
    // Initial: year in the runtime's local timezone (server during SSR, client during hydration with same initial state).
    // After mount on the client, recompute in the browser's timezone — covers SSR-on-UTC-vs-client-TZ edge cases (Dec 31 / Jan 1).
    const [year, setYear] = useState(() => new Date().getFullYear());
    useEffect(() => {
        setYear(new Date().getFullYear());
    }, []);
    return year;
}

export function useIsDark() {
    const [dark, setDark] = useState(false);
    useEffect(() => {
        if (typeof document === "undefined") return;
        const html = document.documentElement;
        const update = () => setDark(html.classList.contains("dark"));
        update();
        const obs = new MutationObserver(update);
        obs.observe(html, { attributes: true, attributeFilter: ["class"] });
        return () => obs.disconnect();
    }, []);
    return dark;
}

export function RevealOnScroll({ children, delay = 0, className = "" }) {
    return (
        <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.2 }}
            variants={fadeUpStagger(delay)}
            className={className}
        >
            {children}
        </motion.div>
    );
}
